import { useCallback, useMemo, type ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  FinancialFormChatSummaryCard,
  FormChatNumberPicker,
  type FinancialChatSummary,
  type FormChatEditableFieldOption,
  type FormChatFieldInput,
  type FormChatListOption,
} from "gsintegrations";
import { trackFinancialEvent } from "../analytics/financial";
import type {
  FinancialReferenceData,
  FormChatMessageRequest,
  FormChatMessageResponse,
  FormChatState,
} from "../repositories/formChat.repository";
import { formChatRepository } from "../repositories/formChat.repository";
import { extractAxiosApiError } from "../utils/extractAxiosApiError";
import {
  readFinancialEditRecord,
  withFinancialEditRecord,
} from "../utils/financialEditRecord";
import type { FinancialChatFlow } from "../utils/financialFormChatPayloadBuilder";
import { useCachedMedium } from "./useCachedMedium";
import { useCachedTerreiro } from "./useCachedTerreiro";
import { useCategoriesAndAccountsQuery } from "./useCategoriesAndAccountsQuery";
import { useFormChatSubmitFinancial } from "./useFormChatSubmitFinancial";
import { useHasPermission } from "./useHasPermission";
import { useIncomeByIdQuery } from "./useIncomeByIdQuery";
import { useOutgoingByIdQuery } from "./useOutgoingByIdQuery";
import { useProductAnalyticsBase } from "./useProductAnalyticsContext";
import { useInventoryListQuery } from "./useTerreiroInventoryListQuery";

const PAYMENT_METHOD_REPLIES = [
  "Dinheiro",
  "Pix",
  "Cartão de débito",
  "Cartão de crédito",
  "Transferência",
  "Boleto",
  "Cheque",
];

const LIST_FIELD_LABELS: Record<string, string> = {
  categoryId: "Ver categorias",
  subcategoryId: "Ver subcategorias",
  accountId: "Ver contas",
  inventoryItemId: "Ver itens do estoque",
};

const LOCAL_FIELD_INPUTS: Record<string, Omit<FormChatFieldInput, "field">> = {
  paymentMethod: { type: "quick_replies", quickReplies: PAYMENT_METHOD_REPLIES },
  settlementStatus: {
    type: "quick_replies",
    quickReplies: ["Pago", "Aguardando pagamento"],
  },
  isRecurring: { type: "quick_replies", quickReplies: ["Sim", "Não"] },
  isSharedWithMembers: { type: "quick_replies", quickReplies: ["Sim", "Não"] },
  amount: { type: "number", numberFormat: "currency", allowFreeText: true },
  installments: { type: "number", numberFormat: "integer", allowFreeText: true },
  purchasedQuantity: {
    type: "number",
    numberFormat: "integer",
    allowFreeText: true,
    quickReplies: ["Pular"],
  },
  shareReason: { type: "text", allowFreeText: true, quickReplies: ["Pular"] },
  observation: { type: "text", allowFreeText: true, quickReplies: ["Pular"] },
};

const NUMERIC_FIELDS = new Set(["amount", "installments", "purchasedQuantity"]);

const QUANTITY_PICKERS: Record<
  string,
  { title: string; label: string; min: number; max: number; fallback: number; skippable: boolean }
> = {
  purchasedQuantity: {
    title: "Quantidade comprada",
    label: "Unidades compradas",
    min: 1,
    max: 1_000_000,
    fallback: 1,
    skippable: true,
  },
  installments: {
    title: "Parcelas",
    label: "Número de parcelas",
    min: 1,
    max: 360,
    fallback: 2,
    skippable: false,
  },
};

const formatAmount = (amount: unknown) =>
  typeof amount === "number"
    ? amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : undefined;

interface UseFinancialFormChatOptions {
  flow: FinancialChatFlow;
  enabled: boolean;
  editTargetId?: string;
  prefillInventoryItemId?: string;
  confirmLabel: string;
  sessionId?: string;
  formState: FormChatState;
  summary?: FinancialChatSummary;
  onResponse: (response: FormChatMessageResponse) => void;
  onError?: (error: { code: string; message: string }) => void;
  onReset: () => void;
  onEdit?: () => void;
}

export function useFinancialFormChatHelpers({
  flow,
  enabled,
  editTargetId,
  prefillInventoryItemId,
  confirmLabel,
  sessionId,
  formState,
  summary,
  onResponse,
  onError,
  onReset,
  onEdit,
}: UseFinancialFormChatOptions) {
  const productBase = useProductAnalyticsBase();
  const { id: terreiroId } = useCachedTerreiro() ?? {};
  const { id: mediumId } = useCachedMedium() ?? {};
  const isOutcome = flow === "outcome";
  const isEdit = Boolean(editTargetId);
  const canShareWithMembers = useHasPermission(
    terreiroId,
    "financial.share_transparency",
  );
  const queryTerreiroId = enabled ? terreiroId : undefined;
  const queryMediumId = enabled ? mediumId : undefined;
  const { categories, bankAccounts } = useCategoriesAndAccountsQuery(
    queryTerreiroId,
    queryMediumId,
    flow,
  );
  const { data: inventoryItems = [] } = useInventoryListQuery(
    isOutcome ? queryTerreiroId : undefined,
    isOutcome ? queryMediumId : undefined,
  );
  const inventoryImageById = useMemo(
    () =>
      new Map(
        (inventoryItems as Array<{ id: string; imageUrl?: string | null }>).map(
          (item) => [item.id, item.imageUrl ?? null],
        ),
      ),
    [inventoryItems],
  );
  const { submitFinancialByChat, navigateBack, limit, isSubmitting } =
    useFormChatSubmitFinancial({ flow, editTargetId, enabled });
  const { data: incomeRecord } = useIncomeByIdQuery(
    flow === "income" ? editTargetId : undefined,
  );
  const { data: outcomeRecord } = useOutgoingByIdQuery(
    flow === "outcome" ? editTargetId : undefined,
  );
  const editRecord = useMemo(
    () => readFinancialEditRecord(flow === "income" ? incomeRecord : outcomeRecord),
    [flow, incomeRecord, outcomeRecord],
  );
  const filledFormState = useMemo(
    () => withFinancialEditRecord(formState, editRecord),
    [editRecord, formState],
  );

  const financialReferenceData = useMemo<FinancialReferenceData>(
    () => ({
      financialCategories: categories.map((category) => ({
        id: category.id,
        name: category.name,
        subcategories: (
          (category.subcategories ?? []) as Array<{ id: string; name: string }>
        ).map((subcategory) => ({
          id: subcategory.id,
          name: subcategory.name,
        })),
      })),
      bankAccounts: bankAccounts.map((account) => ({
        id: account.id,
        name: account.name,
      })),
      ...(isOutcome
        ? {
            inventoryItems: (
              inventoryItems as Array<{ id: string; name: string; unit?: string }>
            ).map((item) => ({ id: item.id, name: item.name, unit: item.unit })),
            canShareWithMembers: Boolean(canShareWithMembers),
            ...(prefillInventoryItemId && !isEdit ? { prefillInventoryItemId } : {}),
          }
        : {}),
    }),
    [
      bankAccounts,
      canShareWithMembers,
      categories,
      inventoryItems,
      isEdit,
      isOutcome,
      prefillInventoryItemId,
    ],
  );

  const { mutateAsync: sendFinancialMutation, isPending } = useMutation({
    mutationKey: ["form-chat", "financial-message", flow],
    mutationFn: async (
      payload: FormChatMessageRequest,
    ): Promise<FormChatMessageResponse> => {
      if (terreiroId) return formChatRepository.sendMessage(terreiroId, payload);
      if (mediumId) return formChatRepository.sendPersonalMessage(mediumId, payload);
      throw new Error("Owner scope required");
    },
    onError: () => {
      trackFinancialEvent("financial_form_chat_submit_error", productBase, {
        transaction_type: flow,
      });
    },
  });

  const sendFinancialRequest = useCallback(
    async (
      payload: Omit<FormChatMessageRequest, "entityType">,
    ): Promise<FormChatMessageResponse | undefined> => {
      if (!terreiroId && !mediumId) return undefined;
      try {
        const response = await sendFinancialMutation({
          entityType: flow,
          referenceData: financialReferenceData,
          ...(editTargetId
            ? { mode: "edit" as const, targetEntityId: editTargetId }
            : {}),
          ...payload,
        });
        onResponse(response);
        return response;
      } catch (err: unknown) {
        onError?.(extractAxiosApiError(err));
        throw err;
      }
    },
    [
      editTargetId,
      financialReferenceData,
      flow,
      mediumId,
      onError,
      onResponse,
      sendFinancialMutation,
      terreiroId,
    ],
  );

  const getFieldOptions = useCallback(
    (field: string | undefined): FormChatListOption[] => {
      const toOptions = (items: Array<{ id: string; name: string }>) =>
        items.map((item) => ({ id: item.id, label: item.name }));
      switch (field) {
        case "categoryId":
          return toOptions(financialReferenceData.financialCategories);
        case "subcategoryId":
          return toOptions(
            financialReferenceData.financialCategories.find(
              (category) => category.id === formState.categoryId,
            )?.subcategories ?? [],
          );
        case "accountId":
          return toOptions(financialReferenceData.bankAccounts ?? []);
        case "inventoryItemId":
          return (financialReferenceData.inventoryItems ?? []).map((item) => ({
            id: item.id,
            label: item.name,
            imageUrl: inventoryImageById.get(item.id) ?? null,
          }));
        default:
          return [];
      }
    },
    [financialReferenceData, formState.categoryId, inventoryImageById],
  );

  const isListField = useCallback(
    (field: string | undefined) => Boolean(field && LIST_FIELD_LABELS[field]),
    [],
  );

  const getFieldInput = useCallback(
    (field: string | undefined): FormChatFieldInput | undefined => {
      if (!field) return undefined;
      if (LIST_FIELD_LABELS[field]) {
        return {
          type: "list_select",
          field,
          searchable: true,
          openListLabel: LIST_FIELD_LABELS[field],
          changeLabel: "Alterar",
          allowFreeText: false,
          topSuggestions: getFieldOptions(field)
            .slice(0, 4)
            .map((option) => option.label),
          ...(field === "inventoryItemId" ? { quickReplies: ["Nenhum"] } : {}),
        };
      }
      if (field === "dueDate") {
        return {
          type: "date",
          field,
          allowFreeText: true,
          quickReplies: isOutcome ? ["Hoje", "Sem vencimento"] : ["Hoje"],
        };
      }
      const local = LOCAL_FIELD_INPUTS[field];
      return local
        ? { ...local, field }
        : { type: "text", field, allowFreeText: true };
    },
    [getFieldOptions, isOutcome],
  );

  /** Numeric fields always use the local input so the chat opens the numeric keyboard. */
  const resolveFieldInput = useCallback(
    (
      field: string | undefined,
      serverFieldInput: FormChatFieldInput | undefined,
    ): FormChatFieldInput | undefined =>
      field && NUMERIC_FIELDS.has(field)
        ? getFieldInput(field)
        : (serverFieldInput ?? getFieldInput(field)),
    [getFieldInput],
  );

  const getFieldPanel = useCallback(
    (params: {
      field: string | undefined;
      isLoading: boolean;
      onValue: (value: number) => void;
      onSkip: () => void;
    }): ReactNode => {
      const config = params.field ? QUANTITY_PICKERS[params.field] : undefined;
      if (!params.field || !config) return undefined;
      const current = Number(filledFormState[params.field as keyof FormChatState]);
      return (
        <FormChatNumberPicker
          key={`financial-${params.field}`}
          title={config.title}
          label={config.label}
          value={Number.isInteger(current) && current >= config.min ? current : config.fallback}
          min={config.min}
          max={config.max}
          isLoading={params.isLoading}
          onConfirm={params.onValue}
          {...(config.skippable
            ? { secondaryActionLabel: "Pular", onSecondaryAction: params.onSkip }
            : {})}
        />
      );
    },
    [filledFormState],
  );

  const editableFields = useMemo(() => {
    const selectedCategory = financialReferenceData.financialCategories.find(
      (category) => category.id === filledFormState.categoryId,
    );
    const fields: Array<{ key: string; label: string }> = [
      { key: "categoryId", label: "Categoria" },
    ];
    if (selectedCategory?.subcategories?.length) {
      fields.push({ key: "subcategoryId", label: "Subcategoria" });
    }
    const isUnitPricedPurchase =
      isOutcome &&
      Boolean(filledFormState.inventoryItemId) &&
      Number(filledFormState.purchasedQuantity) >= 1;
    fields.push(
      { key: "amount", label: isUnitPricedPurchase ? "Preço unitário" : "Valor" },
      { key: "paymentMethod", label: "Forma de pagamento" },
      { key: "accountId", label: "Conta" },
      { key: "settlementStatus", label: "Status" },
      { key: "dueDate", label: isOutcome ? "Vencimento" : "Data" },
      { key: "description", label: "Título" },
    );
    if (isOutcome) {
      fields.push({ key: "isRecurring", label: "Recorrência" });
      if (filledFormState.isRecurring) {
        fields.push({ key: "installments", label: "Parcelas" });
      }
      if (
        (financialReferenceData.inventoryItems?.length ?? 0) > 0 ||
        filledFormState.inventoryItemId
      ) {
        fields.push({ key: "inventoryItemId", label: "Item do estoque" });
      }
      if (filledFormState.inventoryItemId) {
        fields.push({ key: "purchasedQuantity", label: "Quantidade comprada" });
      }
      if (financialReferenceData.canShareWithMembers) {
        fields.push({ key: "isSharedWithMembers", label: "Compartilhar com membros" });
        if (filledFormState.isSharedWithMembers) {
          fields.push({ key: "shareReason", label: "Motivo do compartilhamento" });
        }
      }
    }
    fields.push({ key: "observation", label: "Observação" });
    if (isEdit) fields.push({ key: "correctionReason", label: "Motivo da correção" });
    return fields;
  }, [
    financialReferenceData,
    filledFormState.categoryId,
    filledFormState.inventoryItemId,
    filledFormState.isRecurring,
    filledFormState.isSharedWithMembers,
    filledFormState.purchasedQuantity,
    isEdit,
    isOutcome,
  ]);

  const accountName = useMemo(() => {
    const savedAccountName =
      filledFormState.accountId && filledFormState.accountId === editRecord.accountId
        ? editRecord.accountName
        : undefined;
    return (
      summary?.accountName ||
      bankAccounts.find((account) => account.id === filledFormState.accountId)?.name ||
      savedAccountName
    );
  }, [
    bankAccounts,
    editRecord.accountId,
    editRecord.accountName,
    filledFormState.accountId,
    summary?.accountName,
  ]);

  const editableFieldOptions = useMemo<FormChatEditableFieldOption[]>(() => {
    const yesNo = (value: unknown) =>
      typeof value === "boolean" ? (value ? "Sim" : "Não") : undefined;
    const currentValues: Record<string, string | undefined> = {
      description: summary?.description,
      amount: formatAmount(summary?.unitPrice ?? summary?.amount ?? filledFormState.amount),
      categoryId: summary?.categoryName,
      subcategoryId: summary?.subcategoryName,
      accountId: accountName,
      paymentMethod: summary?.paymentMethodLabel,
      settlementStatus: summary?.settlementStatusLabel,
      dueDate:
        summary?.dueDate ?? (filledFormState.dueDate === "" ? "Sem vencimento" : undefined),
      isRecurring: yesNo(filledFormState.isRecurring),
      installments: filledFormState.installments
        ? String(filledFormState.installments)
        : undefined,
      inventoryItemId:
        summary?.inventoryItemName ??
        (filledFormState.inventoryItemId === "" ? "Nenhum" : undefined),
      purchasedQuantity: filledFormState.purchasedQuantity
        ? String(filledFormState.purchasedQuantity)
        : undefined,
      isSharedWithMembers: yesNo(filledFormState.isSharedWithMembers),
      shareReason: summary?.shareReason,
      observation: summary?.observation,
      correctionReason: summary?.correctionReason,
    };
    return editableFields.map((field) => ({
      key: field.key,
      label: field.label,
      currentValue: currentValues[field.key],
    }));
  }, [accountName, editableFields, filledFormState, summary]);

  const handleConfirmFinancial = useCallback(async () => {
    if (!summary || !sessionId) {
      onError?.({
        code: "FORM_CHAT_INCOMPLETE",
        message: "Revise os dados do lançamento no chat antes de confirmar.",
      });
      return;
    }
    let response: FormChatMessageResponse | undefined;
    try {
      response = await sendFinancialRequest({
        sessionId,
        message: "",
        action: "confirm",
      });
    } catch {
      return;
    }
    if (!response || response.currentStep !== "complete") return;

    try {
      await submitFinancialByChat(
        withFinancialEditRecord({ ...formState, ...response.formState }, editRecord),
      );
      onReset();
    } catch (err: unknown) {
      onError?.(
        extractAxiosApiError(
          err,
          isEdit
            ? "Não foi possível salvar as alterações. Revise os dados e tente novamente."
            : "Não foi possível registrar o lançamento. Revise os dados e tente novamente.",
        ),
      );
    }
  }, [
    editRecord,
    formState,
    isEdit,
    onError,
    onReset,
    sendFinancialRequest,
    sessionId,
    submitFinancialByChat,
    summary,
  ]);

  const transcribeAudio = useCallback(
    async (audio: Blob): Promise<string> => {
      if (terreiroId) {
        const response = await formChatRepository.transcribeAudio(terreiroId, audio, {
          entityType: flow,
          mode: isEdit ? "edit" : "create",
        });
        return response.transcript;
      }
      if (mediumId) {
        return (await formChatRepository.transcribePersonalAudio(mediumId, audio))
          .transcript;
      }
      throw new Error("Owner scope required");
    },
    [flow, isEdit, mediumId, terreiroId],
  );

  const isBusy = isPending || isSubmitting;

  const summaryCard = useMemo(() => {
    if (!summary) return null;
    return (
      <FinancialFormChatSummaryCard
        summary={accountName ? { ...summary, accountName } : summary}
        confirmLabel={confirmLabel}
        isLoading={isBusy}
        onConfirm={handleConfirmFinancial}
        onEdit={onEdit}
      />
    );
  }, [accountName, confirmLabel, handleConfirmFinancial, isBusy, onEdit, summary]);

  return {
    financialReferenceData,
    sendFinancialRequest,
    handleConfirmFinancial,
    summaryCard,
    editableFields,
    editableFieldOptions,
    getFieldOptions,
    getFieldInput,
    resolveFieldInput,
    getFieldPanel,
    isListField,
    transcribeAudio,
    navigateBack,
    limit,
    isPending: isBusy,
  };
}
